import React, { useEffect, useRef, useState } from 'react';
import { Box, type BoxProps } from '@mui/material';

type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'none';

interface RevealProps extends Omit<BoxProps, 'ref'> {
  delay?: number;
  duration?: number;
  direction?: RevealDirection;
  distance?: number;
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

const directionToTransform = (direction: RevealDirection, distance: number): string => {
  switch (direction) {
    case 'up':
      return `translate3d(0, ${distance}px, 0)`;
    case 'down':
      return `translate3d(0, -${distance}px, 0)`;
    case 'left':
      return `translate3d(${distance}px, 0, 0)`;
    case 'right':
      return `translate3d(-${distance}px, 0, 0)`;
    default:
      return 'translate3d(0, 0, 0)';
  }
};

const Reveal: React.FC<RevealProps> = ({
  delay = 0,
  duration = 700,
  direction = 'up',
  distance = 28,
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  once = true,
  sx,
  children,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const prefersReducedMotion = useRef<boolean>(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    const node = ref.current;
    if (!node) return;

    if (prefersReducedMotion.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate3d(0, 0, 0)' : directionToTransform(direction, distance),
        transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: 'opacity, transform',
        ...sx
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export default Reveal;
